import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Printer, RefreshCw, Users2, Eraser, ChevronLeft, ChevronRight, CalendarCheck } from "lucide-react";
import AppointmentDialog from "./AppointmentDialog";
import PlannerBoardGrid, { toMin, fromMin, selectionRange, cellState, STAT_TOOLTIPS } from "./PlannerBoardGrid";
import MultiFilter from "./MultiFilter";
import StudentWeekPlanner from "./StudentWeekPlanner";
import { useDataRefresh } from "@/hooks/useDataRefresh";
import { preferJoseAlfredo } from "@/utils/teacherOrder";
import { studentShortLabel, todayISO, formatDateES } from "@/lib/utils-app";

const teacherCode = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return "";
};

// Convierte las casillas pintadas en franjas contiguas HH:MM → HH:MM.
function rangesFromSlots(set, slots, slotMinutes) {
  const idx = Array.from(set).sort((a, b) => a - b);
  const out = [];
  let i = 0;
  while (i < idx.length) {
    let j = i;
    while (j + 1 < idx.length && idx[j + 1] === idx[j] + 1) j += 1;
    out.push({ start: slots[idx[i]], end: fromMin(toMin(slots[idx[j]]) + slotMinutes) });
    i = j + 1;
  }
  return out;
}

// Recorta las franjas existentes dejando sólo lo que cae FUERA de la ventana
// visible de la rejilla (así repintar un tramo no borra el resto del día).
function keepOutsideWindow(ranges, winStart, winEnd) {
  const out = [];
  for (const r of ranges || []) {
    const s = toMin(r.start);
    const e = toMin(r.end);
    if (s < winStart) out.push({ start: r.start, end: fromMin(Math.min(e, winStart)) });
    if (e > winEnd) out.push({ start: fromMin(Math.max(s, winEnd)), end: r.end });
  }
  return out.filter((x) => toMin(x.end) > toMin(x.start));
}

const LEGEND = [  { cls: "bg-emerald-200", label: "Disponible" },
  { cls: "bg-rose-100", label: "No disponible" },
  { cls: "bg-neutral-400", label: "Ya tiene clase" },
  { cls: "bg-amber-400", label: "Solapa con el profesor" },
  { cls: "bg-amber-600", label: "Bloqueo" },
  { cls: "bg-amber-300", label: "Seleccionado" },
];

function printSheet({ date, teacher, rows }) {
  const body = rows.map((r) => `
    <tr>
      <td><b>${r.label}</b></td>
      <td class="mono">${r.time}–${r.end_time}</td>
      <td class="mono">${r.classes}</td>
      <td>${r.pickup || "—"}</td>
      <td>${r.neighborhood || "—"}</td>
      <td class="mono">${r.cp || "—"}</td>
      <td class="mono">${r.phone || "—"}</td>
      <td></td>
    </tr>`).join("");
  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8">
    <title>Hoja de planificación ${date}</title>
    <style>
      body{font-family:system-ui,-apple-system,Segoe UI,sans-serif;padding:24px;color:#111}
      h1{font-size:18px;margin:0 0 4px}
      .sub{font-size:12px;color:#555;margin-bottom:16px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #bbb;padding:6px 8px;text-align:left}
      th{background:#f1f1f1;font-size:10px;text-transform:uppercase;letter-spacing:.04em}
      .mono{font-family:ui-monospace,Menlo,monospace}
      @media print{body{padding:0}}
    </style></head><body>
    <h1>Hoja de planificación — ${date}</h1>
    <div class="sub">Profesor: ${teacher || "—"} · ${rows.length} sesión(es) seleccionada(s)</div>
    <table><thead><tr>
      <th>Alumno</th><th>Horario</th><th>Clases</th><th>PHR</th><th>Barrio</th>
      <th>CP</th><th>Teléfono</th><th>Observaciones</th>
    </tr></thead><tbody>${body}</tbody></table>
    </body></html>`;
  const w = window.open("", "_blank");
  if (!w) {
    toast.error("El navegador bloqueó la ventana de impresión");
    return;
  }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}

export default function PlannerBoardTab() {
  const [date, setDate] = useState(todayISO());
  const [teacher, setTeacher] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [slotMinutes, setSlotMinutes] = useState(45);
  const [gridStart, setGridStart] = useState("09:00");
  const [gridEnd, setGridEnd] = useState("22:00");
  const [mode, setMode] = useState("citas");
  const [availDraft, setAvailDraft] = useState({});
  const [savingAvail, setSavingAvail] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState({});
  const [grouped, setGrouped] = useState(new Set());
  const [creating, setCreating] = useState(null);
  const [students, setStudents] = useState([]);

  // Filtros de grupo
  const [pickupFilter, setPickupFilter] = useState([]);
  const [cpFilter, setCpFilter] = useState([]);
  const [barrioFilter, setBarrioFilter] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyRemaining, setOnlyRemaining] = useState(true);
  const [onlyGroup, setOnlyGroup] = useState(false);
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [onlyNextExam, setOnlyNextExam] = useState(false);
  const [sortBy, setSortBy] = useState("");
  const [retryMode, setRetryMode] = useState({});
  const [refreshingCal, setRefreshingCal] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const gridRef = useRef(null);

  const shiftDate = (delta) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const scrollGrid = (delta) => {
    if (gridRef.current) gridRef.current.scrollBy({ left: delta, behavior: "smooth" });
  };

  const refreshCalendar = async () => {
    setRefreshingCal(true);
    try {
      const { data: r } = await api.post("/holidays/refresh");
      if ((r.updated_years || []).length === 0) {
        toast.success(`Ya estaba al día: festivos oficiales cargados hasta ${r.valid_until}`);
      } else {
        toast.success(`Calendario actualizado hasta ${r.valid_until} (${r.updated_years.join(", ")})`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefreshingCal(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([          api.get("/teachers", { params: { include_inactive: false } }),
          api.get("/students", { params: { active_only: true } }),
        ]);
        const list = preferJoseAlfredo(
          (t.data || [])
            .filter((x) => x.active !== false)
            .map((x) => ({ code: teacherCode(x.name), label: x.name }))
            .filter((x) => x.code),
        );
        setTeachers(list);
        setTeacher((prev) => prev || list[0]?.code || "");
        setStudents(s.data || []);
      } catch {
        /* silencioso — el tablero funciona sin filtro de profesor */
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/planner-board/data", {
        params: { date, teacher, slot_minutes: slotMinutes, grid_start: gridStart, grid_end: gridEnd },
      });
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [date, teacher, slotMinutes, gridStart, gridEnd]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);
  useDataRefresh(load);

  // Opciones de cada filtro, con el nº de alumnos de cada valor.
  const facets = useMemo(() => {
    const count = (field) => {
      const map = new Map();
      for (const s of data?.students || []) {
        const v = s[field];
        if (v) map.set(v, (map.get(v) || 0) + 1);
      }
      return Array.from(map.entries()).sort((a, b) => String(a[0]).localeCompare(String(b[0]), "es"));
    };
    return {
      phr: count("pickup_name"),
      cp: count("codigo_postal"),
      barrio: count("neighborhood"),
    };
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    let rows = data.students.filter((s) => {
      if (onlyGroup && !grouped.has(s.id)) return false;
      if (pickupFilter.length && !pickupFilter.includes(s.pickup_name)) return false;
      if (cpFilter.length && !cpFilter.includes(String(s.codigo_postal || ""))) return false;
      if (barrioFilter.length && !barrioFilter.includes(s.neighborhood)) return false;
      if (onlyRemaining && (s.stats?.remaining ?? 0) <= 0) return false;
      if (onlyAvailable && s.availability.length === 0) return false;
      if (onlyNextExam && !s.next_exam_date) return false;
      if (q && !studentShortLabel(s).toLowerCase().includes(q)) return false;
      return true;
    });
    // Orden manual por columna (PHR · CP · Barrio · Nombre) si se ha pulsado
    // una cabecera; si no, prioridad: disponibilidad → PHR → cercanía de CP.
    const refCp = Number(cpFilter[0]) || Number(rows[0]?.codigo_postal) || 0;
    if (sortBy) {
      const key = {
        nombre: (s) => studentShortLabel(s),
        phr: (s) => s.pickup_name || "zzz",
        cp: (s) => s.codigo_postal || "99999",
        barrio: (s) => s.neighborhood || "zzz",
        // Los que tienen examen convocado primero, por fecha ascendente.
        proximo_examen: (s) => s.next_exam_date || "9999-12-31",
        ultimo_examen: (s) => s.last_exam_date || "0000-01-01",
      }[sortBy];
      const desc = sortBy === "ultimo_examen";
      return [...rows].sort((a, b) =>
        desc
          ? String(key(b)).localeCompare(String(key(a)), "es")
          : String(key(a)).localeCompare(String(key(b)), "es"));
    }
    rows = [...rows].sort((a, b) => {
      const availA = a.availability.length > 0 ? 0 : 1;
      const availB = b.availability.length > 0 ? 0 : 1;
      if (availA !== availB) return availA - availB;
      const pa = (a.pickup_name || a.neighborhood || "zzz").localeCompare(b.pickup_name || b.neighborhood || "zzz");
      if (pa !== 0) return pa;
      const da = Math.abs((Number(a.codigo_postal) || 99999) - refCp);
      const dbb = Math.abs((Number(b.codigo_postal) || 99999) - refCp);
      if (da !== dbb) return da - dbb;
      return studentShortLabel(a).localeCompare(studentShortLabel(b));
    });
    return rows;
  }, [data, search, pickupFilter, cpFilter, barrioFilter, onlyRemaining, onlyGroup, onlyAvailable, onlyNextExam, grouped, sortBy]);

  const toggleCell = (studentId, idx) => {
    setSelection((prev) => {
      const cur = selectionRange(prev[studentId]);
      if (!cur) return { ...prev, [studentId]: { from: idx, to: idx } };
      if (idx >= cur.from && idx <= cur.to) {
        if (cur.from === cur.to) {
          const next = { ...prev };
          delete next[studentId];
          return next;
        }
        return { ...prev, [studentId]: { from: idx, to: idx } };
      }
      // El rango nunca puede atravesar una clase ya existente del alumno:
      // se recorta al tramo libre contiguo que contiene la casilla pulsada.
      const row = (data?.students || []).find((r) => r.id === studentId);
      const busy = (i) => cellState(row, i, data.slots, slotMinutes, []).busy;
      const from = Math.min(cur.from, idx);
      const to = Math.max(cur.to, idx);
      let f = idx;
      let t = idx;
      while (f - 1 >= from && !busy(f - 1)) f -= 1;
      while (t + 1 <= to && !busy(t + 1)) t += 1;
      return { ...prev, [studentId]: { from: f, to: t } };
    });
  };

  const toggleGroup = (studentId) => {
    setGrouped((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId); else next.add(studentId);
      return next;
    });
  };

  const baseAvailSet = useCallback((row) => {
    const set = new Set();
    (data?.slots || []).forEach((s, i) => {
      const st = toMin(s);
      const en = st + slotMinutes;
      if ((row.availability || []).some((r) => toMin(r.start) <= st && toMin(r.end) >= en)) set.add(i);
    });
    return set;
  }, [data, slotMinutes]);

  const toggleAvail = (studentId, idx) => {
    setAvailDraft((prev) => {
      const row = (data?.students || []).find((r) => r.id === studentId);
      const cur = prev[studentId] ? new Set(prev[studentId]) : baseAvailSet(row);
      if (cur.has(idx)) cur.delete(idx); else cur.add(idx);
      return { ...prev, [studentId]: cur };
    });
  };

  const saveAvailability = async () => {
    const ids = Object.keys(availDraft);
    if (ids.length === 0) return;
    setSavingAvail(true);
    try {
      for (const id of ids) {
        const { data: av } = await api.get(`/students/${id}/availability`);
        const weekly = { ...(av?.weekly_slots || {}) };
        // Conserva lo declarado fuera de la ventana visible de la rejilla.
        const winStart = toMin(data.slots[0]);
        const winEnd = toMin(data.slots[data.slots.length - 1]) + slotMinutes;
        const outside = keepOutsideWindow(weekly[data.weekday_key] || [], winStart, winEnd);
        weekly[data.weekday_key] = [
          ...outside,
          ...rangesFromSlots(availDraft[id], data.slots, slotMinutes),
        ].sort((a, b) => a.start.localeCompare(b.start));
        // Reenvía preferencias / barrio / punto habitual: el PUT los pone a "" si se omiten.
        await api.put(`/students/${id}/availability`, {
          weekly_slots: weekly,
          preferences: av?.preferences ?? "",
          neighborhood: av?.neighborhood ?? "",
          habitual_pickup_location_id: av?.habitual_pickup_location_id ?? "",
        });
      }
      toast.success(`Disponibilidad guardada · ${ids.length} alumno(s)`);
      setAvailDraft({});
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingAvail(false);
    }
  };

  const openOk = (row, span) => {    setCreating({
      student_id: row.id,
      date,
      time: span.time,
      end_time: span.end_time,
      teacher,
      pickup_location: row.pickup_name || row.neighborhood || "",
      pickup_location_end: row.pickup_name || row.neighborhood || "",
    });
  };

  const selectedRows = useMemo(() => {
    if (!data) return [];
    return filtered
      .filter((r) => selection[r.id])
      .map((r) => {
        const sel = selectionRange(selection[r.id]);
        const count = sel.to - sel.from + 1;
        return {
          label: studentShortLabel(r) || r.name,
          time: data.slots[sel.from],
          end_time: fromMin(toMin(data.slots[sel.to]) + slotMinutes),
          classes: count,
          pickup: r.pickup_name,
          neighborhood: r.neighborhood,
          cp: r.codigo_postal,
          phone: r.phone,
        };
      })
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [filtered, selection, data, slotMinutes]);

  const boardData = data ? { ...data, students: filtered } : null;

  return (
    <div className="space-y-4" data-testid="planner-board-tab">
      <div className="card-surface p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-semibold text-neutral-600">
            Día
            <div className="flex items-center gap-1 mt-1">
              <button
                type="button" data-testid="board-date-prev"
                onClick={() => shiftDate(-1)}
                className="h-9 w-8 rounded-lg border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center"
                title="Día anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <input
                type="date" value={date} onChange={(e) => setDate(e.target.value)}
                data-testid="board-date"
                className="h-9 rounded-lg border border-neutral-200 px-2 text-sm font-mono"
              />
              <button
                type="button" data-testid="board-date-next"
                onClick={() => shiftDate(1)}
                className="h-9 w-8 rounded-lg border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center"
                title="Día siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </label>
          <label className="text-xs font-semibold text-neutral-600">
            Profesor
            <select
              value={teacher} onChange={(e) => setTeacher(e.target.value)}
              data-testid="board-teacher"
              className="block mt-1 h-9 rounded-lg border border-neutral-200 px-2 text-sm"
            >
              <option value="">— sin filtrar —</option>
              {teachers.map((t) => (
                <option key={t.code} value={t.code}>{t.label} ({t.code})</option>
              ))}
            </select>
          </label>
          <label className="text-xs font-semibold text-neutral-600">
            Casilla
            <select
              value={slotMinutes} onChange={(e) => setSlotMinutes(Number(e.target.value))}
              data-testid="board-slot-minutes"
              className="block mt-1 h-9 rounded-lg border border-neutral-200 px-2 text-sm"
            >
              <option value={30}>30 min</option>
              <option value={45}>45 min (1 clase)</option>
              <option value={60}>60 min</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-neutral-600">
            Desde
            <input type="time" value={gridStart} onChange={(e) => setGridStart(e.target.value)}
              data-testid="board-grid-start"
              className="block mt-1 h-9 rounded-lg border border-neutral-200 px-2 text-sm font-mono" />
          </label>
          <label className="text-xs font-semibold text-neutral-600">
            Hasta
            <input type="time" value={gridEnd} onChange={(e) => setGridEnd(e.target.value)}
              data-testid="board-grid-end"
              className="block mt-1 h-9 rounded-lg border border-neutral-200 px-2 text-sm font-mono" />
          </label>
          <button type="button" onClick={load} data-testid="board-reload" className="btn-secondary !h-9">
            <RefreshCw className="h-4 w-4" /> Actualizar
          </button>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t border-neutral-100 pt-3">
          <MultiFilter
            label="PHR" testid="board-pickup-filter"
            options={facets.phr} selected={pickupFilter} onChange={setPickupFilter}
          />
          <MultiFilter
            label="CP" testid="board-cp-filter" width="min-w-[120px]"
            options={facets.cp} selected={cpFilter} onChange={setCpFilter}
          />
          <MultiFilter
            label="Barrio" testid="board-barrio-filter"
            options={facets.barrio} selected={barrioFilter} onChange={setBarrioFilter}
          />
          <label className="text-xs font-semibold text-neutral-600">
            Buscar alumno
            <input
              value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nombre o apellido"
              data-testid="board-search"
              className="block mt-1 h-9 w-48 rounded-lg border border-neutral-200 px-2 text-sm"
            />
          </label>
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-700 h-9">
            <input type="checkbox" checked={onlyRemaining} onChange={(e) => setOnlyRemaining(e.target.checked)}
              data-testid="board-only-remaining" className="h-4 w-4 accent-orange-600" />
            Sólo con clases restantes
          </label>
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-700 h-9">
            <input type="checkbox" checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)}
              data-testid="board-only-available" className="h-4 w-4 accent-orange-600" />
            Sólo con disponibilidad hoy
          </label>
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-700 h-9">
            <input type="checkbox" checked={onlyNextExam} onChange={(e) => setOnlyNextExam(e.target.checked)}
              data-testid="board-only-next-exam" className="h-4 w-4 accent-orange-600" />
            Sólo con examen convocado
          </label>
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-700 h-9">
            <input type="checkbox" checked={onlyGroup} onChange={(e) => setOnlyGroup(e.target.checked)}
              data-testid="board-only-group" className="h-4 w-4 accent-orange-600" />
            Sólo el grupo marcado ({grouped.size})
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-neutral-100 pt-3">
          <div className="inline-flex rounded-xl border border-neutral-200 bg-white p-1">
            {[
              { key: "citas", label: "Planificar citas" },
              { key: "disponibilidad", label: "Pintar disponibilidad" },
            ].map((m) => (
              <button
                key={m.key}
                type="button"
                data-testid={`board-mode-${m.key}`}
                onClick={() => setMode(m.key)}
                className={
                  "h-8 px-3 rounded-lg text-[11px] font-bold transition-colors " +
                  (mode === m.key ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100")
                }
              >
                {m.label}
              </button>
            ))}
          </div>
          {mode === "disponibilidad" && (
            <button
              type="button" data-testid="board-save-availability"
              disabled={savingAvail || Object.keys(availDraft).length === 0}
              onClick={saveAvailability}
              className="btn-primary !h-9 disabled:opacity-40"
            >
              {savingAvail ? "Guardando…" : `Guardar disponibilidad (${Object.keys(availDraft).length})`}
            </button>
          )}
          <div className="flex items-center gap-3 text-[11px] text-neutral-600">
            {LEGEND.map((l) => (
              <span key={l.label} className="inline-flex items-center gap-1.5">
                <span className={"inline-block h-3 w-3 rounded " + l.cls} /> {l.label}
              </span>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-neutral-500" data-testid="board-summary">
              {filtered.length} alumnos · {selectedRows.length} seleccionados
            </span>
            <button
              type="button" data-testid="board-clear"
              onClick={() => setSelection({})}
              className="btn-ghost border border-neutral-200 !h-9"
            >
              <Eraser className="h-4 w-4" /> Limpiar
            </button>
            <button
              type="button" data-testid="board-print"
              disabled={selectedRows.length === 0}
              onClick={() => printSheet({ date, teacher, rows: selectedRows })}
              className="btn-secondary !h-9 disabled:opacity-40"
            >
              <Printer className="h-4 w-4" /> Hoja imprimible
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
        <Users2 className="h-4 w-4" />
        <span>
          {formatDateES(date)} — {mode === "disponibilidad"
            ? "pinta en verde las horas libres de cada alumno y pulsa Guardar disponibilidad."
            : <>pinta las horas de cada alumno y pulsa <b className="mx-1">OK</b> para abrir el asistente de cita ya relleno.</>}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {data?.holidays && (
            <span
              data-testid="board-holidays-status"
              className={
                "text-[11px] px-2 py-1 rounded-lg border " +
                (data.holidays.stale
                  ? "border-amber-300 bg-amber-50 text-amber-800 font-semibold"
                  : "border-neutral-200 text-neutral-500")
              }
              title={data.holidays.warning || "Festivos de Móstoles / Madrid usados para contar días hábiles"}
            >
              Festivos hasta {data.holidays.valid_until || "—"}
            </span>
          )}
          <button
            type="button" data-testid="board-refresh-calendar"
            onClick={refreshCalendar} disabled={refreshingCal}
            className="btn-ghost border border-neutral-200 !h-8 text-[11px] disabled:opacity-40"
            title="Consulta por IA los festivos oficiales de los próximos 12 meses"
          >
            <CalendarCheck className="h-3.5 w-3.5" />
            {refreshingCal ? "Actualizando…" : "Actualizar calendario"}
          </button>
          <div className="inline-flex rounded-lg border border-neutral-200 overflow-hidden">
            <button
              type="button" data-testid="board-scroll-left"
              onClick={() => scrollGrid(-480)}
              className="h-8 w-9 flex items-center justify-center hover:bg-neutral-50"
              title="Desplazar la hoja a la izquierda"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button" data-testid="board-scroll-right"
              onClick={() => scrollGrid(480)}
              className="h-8 w-9 flex items-center justify-center hover:bg-neutral-50 border-l border-neutral-200"
              title="Desplazar la hoja a la derecha"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {!boardData ? (
        <div className="card-surface p-10 text-center text-neutral-500">Cargando mesa de planificación…</div>
      ) : (
        <PlannerBoardGrid
          data={boardData}
          selection={selection}
          grouped={grouped}
          slotMinutes={slotMinutes}
          mode={mode}
          availDraft={availDraft}
          onToggleAvail={toggleAvail}
          sortBy={sortBy}
          onSort={(f) => setSortBy((prev) => (prev === f ? "" : f))}
          retryMode={retryMode}
          onRetryMode={(id, m) => setRetryMode((prev) => ({ ...prev, [id]: m }))}
          scrollRef={gridRef}
          expanded={expanded}
          onToggleExpand={(id) => setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          })}
          renderExpanded={(row) => (
            <StudentWeekPlanner
              studentId={row.id}
              teacher={teacher}
              slotMinutes={slotMinutes}
              gridStart={gridStart}
              gridEnd={gridEnd}
              onCreated={load}
            />
          )}
          onToggleCell={toggleCell}
          onToggleGroup={toggleGroup}
          onOk={openOk}
        />
      )}

      <div className="text-[11px] text-neutral-500 leading-relaxed">
        <b>Contadores:</b> {Object.values(STAT_TOOLTIPS).join(" · ")}
        <br />
        <b>Reexamen:</b> 2 = 10 días hábiles · 2+ = 15 días hábiles, contados desde el día
        siguiente al examen y saltando fines de semana y festivos de Móstoles.
      </div>

      <AppointmentDialog
        open={creating !== null}
        appointment={creating}
        initialDate={date}
        students={students}
        onClose={() => setCreating(null)}
        onSaved={() => {
          setCreating(null);
          setSelection((prev) => {
            const next = { ...prev };
            if (creating?.student_id) delete next[creating.student_id];
            return next;
          });
          load();
        }}
      />
    </div>
  );
}
